import React, {useEffect, useState} from "react";
import AdminSidebar from "../components/sidebar/admin_sidebar";
import PageTitle from "../../../components/page_title/page_title";
import {TextInput} from "../../../components/input/inputs";
import TableComponent from "../../../components/table/table";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import styles from "../../settings/styles/settings.module.css";
import sidebarStyles from "../components/sidebar/admin_sidebar.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGripVertical, faPlus, faTrash, faEdit, faStar, faCrown} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';

export default function PaymentPlans() {
    const { t } = useTranslation();
    const [form, setForm] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState({});
    const [rows, setRows] = useState([]);

    // Perks management
    const [perks, setPerks] = useState([]);
    const [newPerkText, setNewPerkText] = useState('');
    const [editingPerkId, setEditingPerkId] = useState(null);
    const [editingPerkText, setEditingPerkText] = useState('');

    const cols = [
        {title: t('admin.columns.name'), internal: 'name', code: (row) => (
            <div className="d-flex align-items-center gap-2">
                {row.name}
                {row.is_popular && <span style={{backgroundColor: '#FEF3C7', color: '#F59E0B', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600'}}>{t('admin.paymentPlans.mostPopular')}</span>}
                {row.is_premium && <span style={{backgroundColor: '#EDE9FE', color: '#7C3AED', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600'}}>{t('admin.paymentPlans.premium')}</span>}
            </div>
        )},
        {title: t('admin.columns.price'), internal: 'price', code: (row) => `$${row.price}`},
        {title: t('admin.columns.annualDiscount'), internal: 'annual_discount_percent', code: (row) => row.annual_discount_percent ? `${row.annual_discount_percent}%` : '-'},
        {title: t('admin.columns.devices'), internal: 'max_devices', code: (row) => row.max_devices === 0 ? t('common.unlimited') : row.max_devices},
        {title: t('admin.columns.companies'), internal: 'max_companies', code: (row) => row.max_companies === 0 ? t('common.unlimited') : row.max_companies},
        {title: t('admin.columns.perks'), internal: 'perks', code: (row) => row.perks?.length || 0},
        {title: t('admin.columns.actions'), internal: '', code: (row) => {
            return (
                <div className={`d-flex flex-row justify-content-center align-items-center ps-3 ${styles.editButton}`} onClick={() => {
                    setShowModal(true);
                    setCurrentPlan(row);
                    setForm({
                        name: row.name,
                        description: row.description,
                        price: row.price,
                        max_devices: row.max_devices,
                        max_companies: row.max_companies,
                        max_users: row.max_users,
                        refresh_rate_seconds: row.refresh_rate_seconds,
                        annual_discount_percent: row.annual_discount_percent || 0,
                        is_popular: row.is_popular || false,
                        is_premium: row.is_premium || false,
                        is_free: row.is_free || false,
                        is_active: row.is_active !== false,
                        allows_gps_tracking: row.allows_gps_tracking || false,
                        allows_scada: row.allows_scada || false,
                        order: row.order || 0
                    });
                    setPerks(row.perks || []);
                }}/>
            )
        }}
    ];

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = () => {
        axiosInstance.get('payment_plan/').then((res) => {
            setRows(res.data.results || res.data);
        });
    };

    const formatApiError = (err, fallback) => {
        const data = err?.response?.data;
        if (!data) return fallback;
        if (typeof data === 'string') return `${fallback}: ${data}`;
        if (data.detail) return `${fallback}: ${data.detail}`;
        const lines = Object.entries(data).map(([field, errors]) => {
            const msg = Array.isArray(errors) ? errors.join(', ') : String(errors);
            return `${field}: ${msg}`;
        });
        return lines.length ? `${fallback}\n${lines.join('\n')}` : fallback;
    };

    const submit = () => {
        const payload = {
            name: form.name,
            description: form.description || '',
            price: parseFloat(form.price) || 0,
            max_devices: parseInt(form.max_devices) || 0,
            max_companies: parseInt(form.max_companies) || 0,
            max_users: parseInt(form.max_users) || 0,
            refresh_rate_seconds: parseFloat(form.refresh_rate_seconds) || 900,
            annual_discount_percent: parseFloat(form.annual_discount_percent) || 0,
            is_popular: form.is_popular || false,
            is_premium: form.is_premium || false,
            is_free: form.is_free || false,
            is_active: form.is_active !== false,
            allows_gps_tracking: form.allows_gps_tracking || false,
            allows_scada: form.allows_scada || false,
            order: parseInt(form.order) || 0
        };

        const onError = (err) => {
            console.error('Error saving plan:', err);
            alert(formatApiError(err, t('admin.paymentPlans.errorSavingPlan')));
        };

        if(Object.keys(currentPlan).length > 0) {
            axiosInstance.put(`payment_plan/${currentPlan.id}/`, payload).then(() => {
                setShowModal(false);
                fetchPlans();
            }).catch(onError);
        } else {
            axiosInstance.post('payment_plan/', payload).then(() => {
                setShowModal(false);
                fetchPlans();
            }).catch(onError);
        }
    };

    // Perk CRUD functions
    const addPerk = () => {
        if (!newPerkText.trim() || !currentPlan.id) return;

        axiosInstance.post('plan_perk/', {
            payment_plan: currentPlan.id,
            text: newPerkText.trim(),
            order: perks.length
        }).then((res) => {
            setPerks([...perks, res.data]);
            setNewPerkText('');
        }).catch((err) => {
            console.error('Error adding perk:', err);
            alert(formatApiError(err, t('admin.paymentPlans.errorAddingPerk')));
        });
    };

    const updatePerk = (perkId) => {
        if (!editingPerkText.trim()) return;

        axiosInstance.put(`plan_perk/${perkId}/`, {
            text: editingPerkText.trim()
        }).then((res) => {
            setPerks(perks.map(p => p.id === perkId ? res.data : p));
            setEditingPerkId(null);
            setEditingPerkText('');
        }).catch((err) => {
            console.error('Error updating perk:', err);
            alert(formatApiError(err, t('admin.paymentPlans.errorUpdatingPerk')));
        });
    };

    const deletePerk = (perkId) => {
        if (!window.confirm(t('admin.paymentPlans.deleteConfirmPerk'))) return;

        axiosInstance.delete(`plan_perk/${perkId}/`).then(() => {
            setPerks(perks.filter(p => p.id !== perkId));
        }).catch((err) => {
            console.error('Error deleting perk:', err);
            alert(formatApiError(err, t('admin.paymentPlans.errorDeletingPerk')));
        });
    };

    const reorderPerk = (index, direction) => {
        const newPerks = [...perks];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newPerks.length) return;

        [newPerks[index], newPerks[targetIndex]] = [newPerks[targetIndex], newPerks[index]];

        // Update order in backend
        newPerks.forEach((perk, i) => {
            axiosInstance.put(`plan_perk/${perk.id}/`, { order: i });
        });

        setPerks(newPerks);
    };

    const openCreateModal = () => {
        setCurrentPlan({});
        setForm({
            annual_discount_percent: 0,
            is_popular: false,
            is_premium: false,
            is_free: false,
            is_active: true,
            allows_gps_tracking: false,
            allows_scada: false,
            order: rows.length
        });
        setPerks([]);
        setShowModal(true);
    };

    return (
        <div className={sidebarStyles.adminContent}>
            <AdminSidebar active={2}/>

            <div className={'mx-5'}>
                <PageTitle title={'Payment Plans'} createName={'Payment Plan'} openModal={openCreateModal}/>

                <Modal
                    show={showModal}
                    setter={setShowModal}
                    name={Object.keys(currentPlan).length > 0 ? t('admin.paymentPlans.editPlan') : t('admin.paymentPlans.createPlan')}
                    onClick={submit}
                    buttonName={Object.keys(currentPlan).length > 0 ? t('common.save') : t('common.create')}
                >
                    {/* Basic Info */}
                    <div style={{marginBottom: '20px'}}>
                        <h6 style={{color: '#64748B', marginBottom: '12px', fontWeight: '600'}}>{t('admin.paymentPlans.basicInfo')}</h6>
                        <TextInput name={'Name'} internalName={'name'} setForm={setForm} form={form}/>
                        <TextInput name={'Description'} internalName={'description'} setForm={setForm} form={form}/>
                        <div className="d-flex gap-3">
                            <div style={{flex: 1}}>
                                <TextInput name={'Price ($)'} internalName={'price'} setForm={setForm} form={form}/>
                            </div>
                            <div style={{flex: 1}}>
                                <TextInput name={'Order'} internalName={'order'} setForm={setForm} form={form}/>
                            </div>
                        </div>
                    </div>

                    {/* Limits */}
                    <div style={{marginBottom: '20px'}}>
                        <h6 style={{color: '#64748B', marginBottom: '12px', fontWeight: '600'}}>{t('admin.paymentPlans.limits')}</h6>
                        <div className="d-flex gap-3">
                            <div style={{flex: 1}}>
                                <TextInput name={'Max Devices'} internalName={'max_devices'} setForm={setForm} form={form}/>
                            </div>
                            <div style={{flex: 1}}>
                                <TextInput name={'Max Companies'} internalName={'max_companies'} setForm={setForm} form={form}/>
                            </div>
                        </div>
                        <div className="d-flex gap-3">
                            <div style={{flex: 1}}>
                                <TextInput name={'Max Users'} internalName={'max_users'} setForm={setForm} form={form}/>
                            </div>
                            <div style={{flex: 1}}>
                                <TextInput name={'Refresh Rate (sec)'} internalName={'refresh_rate_seconds'} setForm={setForm} form={form}/>
                            </div>
                        </div>
                    </div>

                    {/* Pricing & Badges */}
                    <div style={{marginBottom: '20px'}}>
                        <h6 style={{color: '#64748B', marginBottom: '12px', fontWeight: '600'}}>{t('admin.paymentPlans.pricingBadges')}</h6>
                        <TextInput name={'Annual Discount %'} internalName={'annual_discount_percent'} setForm={setForm} form={form} placeholder={'e.g. 20 for 20% off'}/>

                        <div className="d-flex gap-4 mt-3">
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.is_popular || false}
                                    onChange={(e) => setForm({...form, is_popular: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <FontAwesomeIcon icon={faStar} color="#F59E0B"/>
                                <span>{t('admin.paymentPlans.mostPopular')}</span>
                            </label>
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.is_premium || false}
                                    onChange={(e) => setForm({...form, is_premium: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <FontAwesomeIcon icon={faCrown} color="#7C3AED"/>
                                <span>{t('admin.paymentPlans.premium')}</span>
                            </label>
                        </div>

                        <div className="d-flex gap-4 mt-3 flex-wrap">
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.is_free || false}
                                    onChange={(e) => setForm({...form, is_free: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <span>{t('admin.paymentPlans.freePlan')}</span>
                            </label>
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.is_active !== false}
                                    onChange={(e) => setForm({...form, is_active: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <span>{t('admin.paymentPlans.active')}</span>
                            </label>
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.allows_gps_tracking || false}
                                    onChange={(e) => setForm({...form, allows_gps_tracking: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <span>{t('admin.paymentPlans.gpsTracking')}</span>
                            </label>
                            <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                                <input
                                    type="checkbox"
                                    checked={form.allows_scada || false}
                                    onChange={(e) => setForm({...form, allows_scada: e.target.checked})}
                                    style={{width: '18px', height: '18px'}}
                                />
                                <span>SCADA</span>
                            </label>
                        </div>
                    </div>

                    {/* Perks Section - Only show for existing plans */}
                    {currentPlan.id && (
                        <div style={{marginBottom: '20px'}}>
                            <h6 style={{color: '#64748B', marginBottom: '12px', fontWeight: '600'}}>
                                {t('admin.paymentPlans.planBenefits', {count: perks.length})}
                            </h6>

                            {/* Existing Perks */}
                            <div style={{maxHeight: '200px', overflowY: 'auto', marginBottom: '12px'}}>
                                {perks.sort((a, b) => a.order - b.order).map((perk, index) => (
                                    <div
                                        key={perk.id}
                                        className="d-flex align-items-center gap-2 mb-2"
                                        style={{
                                            padding: '8px 12px',
                                            backgroundColor: '#F8FAFC',
                                            borderRadius: '6px',
                                            border: '1px solid #E2E8F0'
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faGripVertical}
                                            color="#94A3B8"
                                            style={{cursor: 'grab'}}
                                        />

                                        {editingPerkId === perk.id ? (
                                            <>
                                                <input
                                                    type="text"
                                                    value={editingPerkText}
                                                    onChange={(e) => setEditingPerkText(e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        border: '1px solid #4F46E5',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        fontSize: '14px'
                                                    }}
                                                    onKeyPress={(e) => e.key === 'Enter' && updatePerk(perk.id)}
                                                />
                                                <button
                                                    onClick={() => updatePerk(perk.id)}
                                                    style={{
                                                        backgroundColor: '#4F46E5',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 12px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {t('common.save')}
                                                </button>
                                                <button
                                                    onClick={() => {setEditingPerkId(null); setEditingPerkText('');}}
                                                    style={{
                                                        backgroundColor: '#E2E8F0',
                                                        color: '#64748B',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 12px',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {t('common.cancel')}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{flex: 1, fontSize: '14px'}}>{perk.text}</span>
                                                <button
                                                    onClick={() => {setEditingPerkId(perk.id); setEditingPerkText(perk.text);}}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} color="#64748B"/>
                                                </button>
                                                <button
                                                    onClick={() => deletePerk(perk.id)}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} color="#EF4444"/>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}

                                {perks.length === 0 && (
                                    <div style={{textAlign: 'center', color: '#94A3B8', padding: '20px'}}>
                                        {t('admin.paymentPlans.noBenefits')}
                                    </div>
                                )}
                            </div>

                            {/* Add New Perk */}
                            <div className="d-flex gap-2">
                                <input
                                    type="text"
                                    value={newPerkText}
                                    onChange={(e) => setNewPerkText(e.target.value)}
                                    placeholder={t('admin.paymentPlans.addBenefit')}
                                    style={{
                                        flex: 1,
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        fontSize: '14px'
                                    }}
                                    onKeyPress={(e) => e.key === 'Enter' && addPerk()}
                                />
                                <button
                                    onClick={addPerk}
                                    disabled={!newPerkText.trim()}
                                    style={{
                                        backgroundColor: newPerkText.trim() ? '#4F46E5' : '#E2E8F0',
                                        color: newPerkText.trim() ? 'white' : '#94A3B8',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        cursor: newPerkText.trim() ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus}/>
                                    {t('common.add')}
                                </button>
                            </div>

                            <p style={{fontSize: '12px', color: '#94A3B8', marginTop: '8px'}}>
                                {t('admin.paymentPlans.savePlanFirst')}
                            </p>
                        </div>
                    )}

                    {!currentPlan.id && (
                        <div style={{
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            padding: '12px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            marginBottom: '12px'
                        }}>
                            {t('admin.paymentPlans.savePlanFirst')}
                        </div>
                    )}
                </Modal>

                <div className={'mt-3'}>
                    <TableComponent
                        cols={cols}
                        rows={rows}
                    />
                </div>
            </div>
        </div>
    );
}
